import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import dayjs from 'dayjs';
import { apiClient, type MetaConfig } from '../utils/api';
import { useTelegram } from '../hooks/useTelegram';

const incomeSchema = z.object({
  source: z.string().min(1, 'Источник обязателен'),
  customSource: z.string().optional(),
  amount: z.number({ required_error: 'Сумма обязательна' }).min(0.01, 'Сумма должна быть больше 0'),
  currency: z.string().min(1, 'Валюта обязательна'),
  account: z.string().min(1, 'Счет обязателен'),
  transaction_time: z.string().min(1, 'Время транзакции обязательно'),
  note: z.string().optional(),
});

type IncomeFormData = z.infer<typeof incomeSchema>;

interface Props {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const incomeSources = [
  { id: 'xsolla', name: 'Xsolla', icon: '🎮', account: 'xsolla' },
  { id: 'stripe', name: 'Stripe', icon: '💳', account: 'stripe' },
  { id: 'fkwallet', name: 'FKWallet', icon: '💰', account: 'fkwallet' },
  { id: 'custom', name: 'Другой источник', icon: '📝', account: '' }
];

export function IncomeForm({ onSuccess, onCancel }: Props) {
  const [meta, setMeta] = useState<MetaConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { tg } = useTelegram();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm<IncomeFormData>({
    resolver: zodResolver(incomeSchema),
    defaultValues: {
      source: '',
      customSource: '',
      amount: 0,
      currency: 'USD',
      account: '',
      transaction_time: dayjs().format('YYYY-MM-DDTHH:mm'),
      note: '',
    },
  });

  const watchedSource = watch('source');

  useEffect(() => {
    loadMeta();
  }, []);

  // Автозаполнение счета при выборе источника
  useEffect(() => {
    const selectedSource = incomeSources.find(s => s.id === watchedSource);
    if (selectedSource && selectedSource.account) {
      setValue('account', selectedSource.account);
    }
  }, [watchedSource, setValue]);

  useEffect(() => {
    if (tg) {
      tg.MainButton.setText('Добавить приход');
      tg.MainButton.onClick(handleMainButtonClick);
      tg.MainButton.show();

      return () => {
        tg.MainButton.offClick(handleMainButtonClick);
        tg.MainButton.hide();
      };
    }
  }, [tg]);

  const loadMeta = async () => {
    try {
      const data = await apiClient.getMeta();
      setMeta(data);
    } catch (error) {
      console.error('Failed to load meta:', error);
    }
  };

  const handleMainButtonClick = () => {
    handleSubmit(onSubmit)();
  };

  const onSubmit = async (data: IncomeFormData) => {
    setIsLoading(true);
    try {
      const selectedSource = incomeSources.find(s => s.id === data.source);
      const sourceName = data.source === 'custom' ? (data.customSource || 'Другой источник') : selectedSource?.name;

      const transactionData = {
        name: `Донат за месяц ${sourceName}`,
        money: Math.abs(data.amount), // Всегда положительное число для прихода
        fee: 0, // Комиссия всегда 0 для прихода
        currency: data.currency,
        account: data.account,
        executor: 'none', // Исполнитель всегда 'none' для прихода
        transaction_time: data.transaction_time,
        destination_time: data.transaction_time,
        category: 'приход',
        payment: '',
        note: data.note || '',
      };

      await apiClient.createTransaction(transactionData);

      // Показываем анимацию успеха
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        reset();
        onSuccess?.();
      }, 2000);

    } catch (error) {
      console.error('Error saving transaction:', error);
      alert(`Ошибка: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!meta) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin w-8 h-8 border-4 border-telegram-button border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-telegram-hint">Загрузка...</p>
      </div>
    );
  }

  if (showSuccess) {
    return (
      <div className="p-6 text-center">
        <div className="mb-6">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
            <span className="text-2xl">✅</span>
          </div>
          <h3 className="text-xl font-semibold text-emerald-600 mb-2">Приход добавлен!</h3>
          <p className="text-telegram-hint">Транзакция успешно создана</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <span className="text-xl">💰</span>
        </div>
        <h2 className="text-xl font-bold text-telegram-text mb-1">Новый приход</h2>
        <p className="text-sm text-telegram-hint">Поступление средств на счет</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

        {/* Источник прихода */}
        <div>
          <label className="block text-sm font-medium text-telegram-text mb-3">
            Источник поступления
          </label>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {incomeSources.map((source) => (
              <button
                key={source.id}
                type="button"
                onClick={() => {
                  setValue('source', source.id);
                  // Счет будет автоматически заполнен через useEffect
                }}
                className={`
                  p-4 rounded-xl border-2 transition-all duration-200
                  ${watchedSource === source.id
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                  }
                `}
              >
                <div className="text-center">
                  <div className="text-2xl mb-2">{source.icon}</div>
                  <div className={`text-sm font-medium ${
                    watchedSource === source.id ? 'text-emerald-700' : 'text-gray-700'
                  }`}>
                    {source.name}
                  </div>
                  {source.account && (
                    <div className={`text-xs mt-1 ${
                      watchedSource === source.id ? 'text-emerald-600' : 'text-gray-500'
                    }`}>
                      → {source.account}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
          {errors.source && <p className="text-red-500 text-xs mt-2">{errors.source.message}</p>}
        </div>

        {/* Кастомный источник */}
        {watchedSource === 'custom' && (
          <div className="animate-fadeIn">
            <label className="block text-sm font-medium text-telegram-text mb-2">
              Название источника
            </label>
            <input
              {...register('customSource')}
              className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
              placeholder="Введите название источника"
            />
            {errors.customSource && <p className="text-red-500 text-xs mt-1">{errors.customSource.message}</p>}
          </div>
        )}

        {/* Сумма */}
        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Сумма поступления
          </label>
          <div className="relative">
            <input
              {...register('amount', { valueAsNumber: true })}
              type="number"
              step="0.01"
              min="0"
              className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-lg font-semibold"
              placeholder="0.00"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-600 font-medium">
              {watch('currency')}
            </div>
          </div>
          {errors.amount && <p className="text-red-500 text-xs mt-1">{errors.amount.message}</p>}
        </div>

        {/* Валюта */}
        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Валюта
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {meta.currencies.map((currency) => (
              <button
                key={currency}
                type="button"
                onClick={() => setValue('currency', currency)}
                className={`
                  p-3 rounded-lg border transition-all duration-200 font-medium
                  ${watch('currency') === currency
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {currency}
              </button>
            ))}
          </div>
          {errors.currency && <p className="text-red-500 text-xs mt-1">{errors.currency.message}</p>}
        </div>

        {/* Счет назначения */}
        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Счет поступления
            {watchedSource && watchedSource !== 'custom' && (
              <span className="ml-2 text-xs text-emerald-600 bg-emerald-100 px-2 py-1 rounded-full">
                Автозаполнено
              </span>
            )}
          </label>
          {watchedSource && watchedSource !== 'custom' ? (
            <div className="w-full p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 font-medium">
              {watch('account') || 'Загрузка...'}
            </div>
          ) : (
            <select
              {...register('account')}
              className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
            >
              <option value="">Выберите счет</option>
              {meta.accounts.map((account) => (
                <option key={account} value={account}>
                  {account}
                </option>
              ))}
            </select>
          )}
          {errors.account && <p className="text-red-500 text-xs mt-1">{errors.account.message}</p>}
        </div>

        {/* Время */}
        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Время поступления
          </label>
          <input
            {...register('transaction_time')}
            type="datetime-local"
            className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
          />
          {errors.transaction_time && (
            <p className="text-red-500 text-xs mt-1">{errors.transaction_time.message}</p>
          )}
        </div>

        {/* Примечание */}
        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Примечание <span className="text-gray-400">(необязательно)</span>
          </label>
          <textarea
            {...register('note')}
            className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all resize-none"
            rows={3}
            placeholder="Дополнительная информация о поступлении"
          />
        </div>

        {/* Кнопки */}
        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 bg-emerald-500 text-white py-4 px-6 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:bg-emerald-600 active:scale-95"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                Создание...
              </div>
            ) : (
              '💰 Добавить приход'
            )}
          </button>

          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-4 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 active:scale-95 transition-all"
            >
              Отмена
            </button>
          )}
        </div>
      </form>
    </div>
  );
}